import { useState } from "react";
import { ArrowRight, Calendar, Send, Globe2, Phone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function Contact() {
  return (
    <div className="pt-16">
      <section id="contact" className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="eyebrow">Contact</div>
            <h1 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Let's talk about your project</h1>
            <p className="mt-4 text-[#B6B0C7]">Pick a date for a 30-minute consultation, or drop us a brief.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4 mb-10">
            <a href="mailto:info@zerolabsdigi.in" data-testid="contact-info-email" className="card p-6 flex items-start gap-3 hover:border-[#22D3EE]/40">
              <div className="w-10 h-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center shrink-0"><Send size={18}/></div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8C859E] font-semibold">Email</div>
                <div className="mt-1 text-white font-semibold break-all">info@zerolabsdigi.in</div>
                <div className="text-[#B6B0C7] text-xs mt-1">We reply within 24 hours</div>
              </div>
            </a>
            <a href="tel:+918971452224" data-testid="contact-info-phone" className="card p-6 flex items-start gap-3 hover:border-[#3B82F6]/40">
              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center shrink-0"><Phone size={18}/></div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8C859E] font-semibold">Phone</div>
                <div className="mt-1 text-white font-semibold">+91 8971452224</div>
                <div className="text-[#B6B0C7] text-xs mt-1">Mon–Sat · 9 AM – 7 PM IST</div>
              </div>
            </a>
            <div data-testid="contact-info-address" className="card p-6 flex items-start gap-3 md:col-span-2">
              <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center shrink-0"><Globe2 size={18}/></div>
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#8C859E] font-semibold">Registered & Corporate Office</div>
                <div className="mt-1 text-white font-semibold">ZeroLabs Digitals</div>
                <div className="text-[#B6B0C7] text-sm mt-1 leading-relaxed">
                  4172, 3rd Floor, 17th Main, 5th Cross, MCC B Block,<br/>
                  Davangere — 577004, Karnataka, India.
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <BookingForm />
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "Web Application Development",
    preferred_date: "", preferred_time: "10:00", notes: "",
  });
  const [status, setStatus] = useState({ kind: "idle" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ kind: "loading" });
    try {
      await api.post("/bookings", form);
      setStatus({ kind: "success", msg: "Thanks! We'll confirm your slot by email shortly." });
      setForm({ ...form, name: "", email: "", phone: "", company: "", notes: "" });
    } catch (err) {
      setStatus({ kind: "error", msg: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div id="book" className="card p-8 glow-border" data-testid="booking-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center"><Calendar size={18}/></div>
        <div>
          <div className="eyebrow">Book a call</div>
          <h3 className="text-2xl font-bold text-white">Free 30-min consultation</h3>
        </div>
      </div>
      <p className="text-[#B6B0C7] text-sm">Pick a date — our team will confirm within one business day.</p>

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
        <input data-testid="booking-name" required value={form.name} onChange={update("name")} className="input-field" placeholder="Your name"/>
        <input data-testid="booking-email" required type="email" value={form.email} onChange={update("email")} className="input-field" placeholder="Work email"/>
        <input data-testid="booking-phone" value={form.phone} onChange={update("phone")} className="input-field" placeholder="Phone (optional)"/>
        <input data-testid="booking-company" value={form.company} onChange={update("company")} className="input-field" placeholder="Company (optional)"/>
        <select data-testid="booking-service" value={form.service} onChange={update("service")} className="input-field">
          {["Web Application Development", "Mobile Application Development", "AI Automation Services", "Plant Engineering Design", "Product Demo"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="booking-date" required type="date" value={form.preferred_date} onChange={update("preferred_date")} className="input-field" min={new Date().toISOString().slice(0,10)}/>
          <input data-testid="booking-time" required type="time" value={form.preferred_time} onChange={update("preferred_time")} className="input-field"/>
        </div>
        <textarea data-testid="booking-notes" value={form.notes} onChange={update("notes")} className="input-field sm:col-span-2" placeholder="Tell us about your project (optional)"/>
        <button data-testid="booking-submit" disabled={status.kind === "loading"} className="btn-primary sm:col-span-2 justify-center disabled:opacity-60">
          {status.kind === "loading" ? "Booking…" : (<>Reserve slot <ArrowRight size={16}/></>)}
        </button>
        {status.kind === "success" && (
          <div data-testid="booking-success" className="sm:col-span-2 text-sm text-[#22D3EE] bg-[#22D3EE]/5 border border-[#22D3EE]/20 rounded-lg p-3">{status.msg}</div>
        )}
        {status.kind === "error" && (
          <div data-testid="booking-error" className="sm:col-span-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{status.msg}</div>
        )}
      </form>
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "+91 ", company: "", service: "", message: "" });
  const [status, setStatus] = useState({ kind: "idle" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ kind: "loading" });
    try {
      // Normalise phone: strip spaces; keep leading + if present
      const phone = (form.phone || "").replace(/\s+/g, "");
      await api.post("/contact", { ...form, phone });
      setStatus({ kind: "success", msg: "Thanks — our AI voice assistant will call you shortly. We also reply to every message within 24 hours." });
      setForm({ name: "", email: "", phone: "+91 ", company: "", service: "", message: "" });
    } catch (err) {
      setStatus({ kind: "error", msg: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div className="card p-8" data-testid="contact-card">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center"><Send size={18}/></div>
        <div>
          <div className="eyebrow">Reach out</div>
          <h3 className="text-2xl font-bold text-white">Tell us what you're building</h3>
        </div>
      </div>
      <p className="text-[#B6B0C7] text-sm">Not ready to book? Drop us a note — we read every message.</p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="contact-name" required value={form.name} onChange={update("name")} className="input-field" placeholder="Your name"/>
          <input data-testid="contact-email" required type="email" value={form.email} onChange={update("email")} className="input-field" placeholder="Work email"/>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="contact-phone" required type="tel" inputMode="tel" pattern="^\+?[0-9 ()\-]{7,20}$" value={form.phone} onChange={update("phone")} className="input-field" placeholder="Phone (with country code)"/>
          <input data-testid="contact-company" value={form.company} onChange={update("company")} className="input-field" placeholder="Company (optional)"/>
        </div>
        <input data-testid="contact-service" value={form.service} onChange={update("service")} className="input-field" placeholder="Service you're interested in"/>
        <textarea data-testid="contact-message" required value={form.message} onChange={update("message")} className="input-field" placeholder="What problem can we solve for you?"/>

        <div data-testid="contact-ai-disclosure" className="flex items-start gap-2 text-xs text-[#B6B0C7] bg-[#3B82F6]/[0.06] border border-[#3B82F6]/20 rounded-lg p-3">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-1.5 shrink-0 animate-pulse"/>
          <span>By submitting, our AI voice assistant <span className="text-[#3B82F6] font-semibold">Zero</span> will call you shortly on the number you provide to understand your project better. Standard call charges may apply. You can ask Zero to stop at any time.</span>
        </div>

        <button data-testid="contact-submit" disabled={status.kind === "loading"} className="btn-primary w-full justify-center disabled:opacity-60">
          {status.kind === "loading" ? "Sending…" : (<>Send message <Send size={14}/></>)}
        </button>
        {status.kind === "success" && (
          <div data-testid="contact-success" className="text-sm text-[#22D3EE] bg-[#22D3EE]/5 border border-[#22D3EE]/20 rounded-lg p-3">{status.msg}</div>
        )}
        {status.kind === "error" && (
          <div data-testid="contact-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{status.msg}</div>
        )}
      </form>
    </div>
  );
}
